<template>
    <div class="AllQuestions">
        <p class="faded">
            {{ introText }}
        </p>
        <section class="InterviewCard__steps">
            <QuestionsListTile
                v-for="question in sortedQuestions"
                :key="question.attrType"
                :question="question"
            />
        </section>
        <div v-if="hasUsIncodeDocument" class="AllQuestions__acceptedDocuments">
            <p class="AllQuestions__acceptedDocumentsLabel">
                Accepted documents:
            </p>
            <ul class="AllQuestions__acceptedDocumentsList">
                <li
                    v-for="doc in usAcceptedDocuments"
                    :key="doc"
                    class="AllQuestions__acceptedDocumentsItem"
                >
                    <var>{{ doc }}</var>
                </li>
            </ul>
        </div>
        <em v-if="numberOfRequiredQuestions > 0">
            <span class="fg-danger">*</span> Required information
        </em>

        <div v-if="returnUrl" class="InterviewCard__returnSection">
            <a
                :href="returnUrl"
                class="Button Button--glass-grey"
                data-title="You can come back and complete this request when you are ready."
            >
                Return
            </a>
        </div>
    </div>
</template>

<style lang="scss">
    .AllQuestions {
        &__acceptedDocuments {
            margin-top: 16px;
        }

        &__acceptedDocumentsLabel {
            margin: 0 0 4px;
            font-size: var(--text-description-size, 0.75rem);
        }

        &__acceptedDocumentsList {
            margin: 0;
            padding-left: 20px;
        }

        &__acceptedDocumentsItem {
            font-size: var(--text-description-size, 0.75rem);
        }
    }
</style>

<script lang="ts">
    import { Component, Vue } from 'vue-property-decorator';

    import semanticQuestionSort from '@evidentid/ido-lib/views/semanticQuestionsSort';

    import { isUsIncodeDocumentQuestion, US_ACCEPTED_DOCUMENTS } from '@/config/incode';
    import { isGlsSession } from '@/interfaces/SessionData';
    import { useIdoStore } from '@/store/store';

    import PrivacyNotice from './PrivacyNotice.vue';
    import QuestionsListTile from './QuestionsListTile.vue';

    @Component({
        components: { PrivacyNotice, QuestionsListTile },
    })
    export default class QuestionsList extends Vue {
        private get numberOfRequiredQuestions() {
            return this.questions
                .reduce((acc, question) => (question.metadata.required ? acc + 1 : acc), 0);
        }

        private get returnUrl() {
            const session = useIdoStore().session;

            if (!session || isGlsSession(session) || !session.enableReturn) {
                return null;
            }

            return session.continueUrl || null;
        }

        private get questions() {
            return useIdoStore().questions;
        }

        private get visibleQuestions() {
            return this.questions.filter((question) => !question.metadata.hidden);
        }

        private get sortedQuestions() {
            return semanticQuestionSort(this.visibleQuestions);
        }

        private get hasUsIncodeDocument(): boolean {
            // Check all questions, not just visible ones — the condensed liveness-incode
            // question (carrying metadata.isUsOnly) may still be hidden behind a pending consent gate.
            return this.questions.some(isUsIncodeDocumentQuestion);
        }

        private usAcceptedDocuments = US_ACCEPTED_DOCUMENTS;

        private get introText() {
            return `Action required to complete your ${useIdoStore().summary || 'profile'}`;
        }
    }
</script>
